import { FileType, ILibraryDocumentListItem } from "@/api";
import { SizeDisplay } from "@/components/shared/SizeDisplay";
import { Badge } from "@/components/ui/badge";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { ColumnDef } from "@tanstack/react-table";
import { formatDistanceToNowStrict } from "date-fns";
import { FileSpreadsheet, FileText } from "lucide-react";
import type { ReactNode } from "react";
import type { DocumentStatus } from "./StatusBadge";
import { StatusBadge } from "./StatusBadge";

function getDocumentTypeIcon(fileType: FileType | null | undefined): ReactNode {
    const isSpreadsheet = fileType === "xlsx" || fileType === "csv";
    const Icon = isSpreadsheet ? FileSpreadsheet : FileText;
    return (
        <span
            role="img"
            aria-label={isSpreadsheet ? "Spreadsheet" : "PDF"}
            className="inline-flex shrink-0"
        >
            <Icon
                size={18}
                strokeWidth={1.8}
                className={
                    isSpreadsheet
                        ? "text-emerald-600/80 dark:text-emerald-400/80"
                        : "text-destructive/70"
                }
                aria-hidden
            />
        </span>
    );
}

export const columns: ColumnDef<ILibraryDocumentListItem>[] = [
    {
        accessorKey: "document_name",
        header: "Name",
        enableSorting: true,
        cell: ({ row }) => {
            const name = row.getValue("document_name") as string;
            const id = row.original.id;
            return (
                <div className="flex min-w-0 items-center gap-2.5">
                    {getDocumentTypeIcon(row.original.file_type)}
                    <div className="min-w-0">
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger className="block max-w-full truncate text-left font-medium">
                                    {name}
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>{name}</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                        <div className="text-muted-foreground font-mono text-xs">
                            #{id}
                        </div>
                    </div>
                </div>
            );
        },
        size: 300,
    },
    {
        accessorKey: "organization_name",
        header: "Organization",
        enableSorting: false,
        cell: ({ row }) => {
            const name = row.getValue("organization_name") as string | null;
            if (!name) {
                return <span className="text-muted-foreground text-xs">—</span>;
            }

            return <Badge variant="secondary">{name}</Badge>;
        },
        size: 160,
    },
    {
        accessorKey: "created_at",
        header: "Upload Date",
        enableSorting: true,
        cell: ({ row }) => {
            const date = row.getValue("created_at") as string;
            return (
                <span
                    className="text-muted-foreground text-sm"
                    title={new Date(date).toLocaleString()}
                >
                    {formatDistanceToNowStrict(date, { addSuffix: true })}
                </span>
            );
        },
        size: 120,
    },
    {
        id: "size",
        header: "Size",
        enableSorting: false,
        cell: ({ row }) => (
            <SizeDisplay
                fileSizeBytes={row.original.file_size_bytes}
                pageCount={row.original.page_count}
            />
        ),
        size: 140,
    },
    {
        accessorKey: "textract_status",
        header: "Status",
        enableSorting: false,
        cell: ({ row }) => {
            const status = row.getValue(
                "textract_status"
            ) as DocumentStatus | null;
            return <StatusBadge status={status} />;
        },
        size: 120,
    },
];
